/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext, useMemo } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Image } from 'ui/atoms/Image';
import { Icon } from 'ui/atoms/Icon/Icon';
import { Badge } from 'ui/atoms/Badge/Badge';
import type { BadgeVariant } from 'ui/atoms/Badge/Badge.types';

const blogTypeVariant: Record<string, BadgeVariant> = {
  company_registry_guides: 'indigo',
  kyb_compliance: 'rose',
  ownership_corporate_structure: 'violet',
  financials_credit_risk: 'emerald',
  company_data_ai: 'cyan',
  tax_regulatory_insights: 'orange',
  platforms_comparisons: 'warning',
  global_database_updates: 'primary',
  top_gdb_companies: 'success',
};

const caseStudyTypeVariant: Record<string, BadgeVariant> = {
  category_1: 'orange',
  category_2: 'indigo',
  category_3: 'emerald',
};

interface BlogItemProps {
  className?: string;
  img?: string;
  title?: string;
  type?: string;
  caseStudyType?: string;
  description?: string;
  readTime?: string;
  slug?: string;
  hasBadge?: boolean;
}

export const BlogItem = ({
  className,
  title,
  img,
  type,
  caseStudyType,
  description,
  readTime,
  slug,
  hasBadge = true,
}: BlogItemProps) => {
  const { t } = useInt();

  const { pageVars } = useContext(ContentContext);

  const commonPageVars = useMemo(() => pageVars.common || {}, [pageVars.common]);

  const label = caseStudyType ? commonPageVars[`case_studies_${caseStudyType}`] : commonPageVars[`blog_${type}`];

  const badgeVariant: BadgeVariant = caseStudyType
    ? (caseStudyTypeVariant[caseStudyType] ?? 'primary')
    : (blogTypeVariant[type ?? ''] ?? 'primary');

  return (
    <Link className={cn('advantages__blocks', className)} route={slug || ''} ariaLabel={title}>
      {img && (
        <div className={cn('advantages__blocks-img-wrap', { 'advantages__blocks-img-wrap--contain': caseStudyType })}>
          <Image fill style={{ objectFit: 'contain' }} width={384} height={260} src={img} alt={title} />
        </div>
      )}

      <div className="advantages__blocks-content">
        <div className="advantages__blocks-meta">
          {label && hasBadge && (
            <Badge variant={badgeVariant} size="sm" className="advantages__blocks-badge">
              {label}
            </Badge>
          )}

          {readTime && (
            <div className="advantages__blocks-read-time">
              <Icon type="duration" size="small" />
              <span>
                {readTime} {commonPageVars.min_read || t('min read')}
              </span>
            </div>
          )}
        </div>

        <h2 className="advantages__blocks-title">{title}</h2>

        {description && (
          <div className="advantages__blocks-description" dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </div>
    </Link>
  );
};
